import type { CatalogImage, CatalogLabels, CatalogPage, CatalogPrice, ValidationStatus } from './common'
import type { Department, Family, Subdepartment, Universe } from '@/types/treeViewTypes'

export type ViewMode = 'sm' | 'model'

export interface ProductComposition {
  id: string
  part_of_product: {
    id: string
    ddfs_id: string
    rate: number
    raw_materials: {
      id: string
      ddfs_id: string
      rate: number
    }[]
  }
}

export interface ProductBenefit {
  id: string
  value: number
}

export interface ProductReview {
  average_rating: number
  total: number
}

export interface GalleryProductBase {
  availability_date: {
    start_date: string
    end_date: string
  }
  brand: { id: string }
  co2_environmental_impact: {
    unit: string
    total: number
  }
  colors?: ProductColors
  composition: ProductComposition[]
  conception_code: { id: string }
  department: Department
  description?: CatalogLabels
  design_center: { id: string }
  dmi_segmentation?: {
    type: string
    value: string
    technicity_level: '100' | '500' | '900' | 'ALL_LEVELS'
  }
  eco_designed: boolean
  family: Family
  flavors?: [{ order: string, id: string }]
  fragrance?: string
  genders: { id: string }[]
  image: CatalogImage
  is_new: boolean
  manufacture?: ProductManufacture
  price: CatalogPrice
  product_benefits: ProductBenefit[]
  product_nature: { id: string }
  review?: ProductReview | null
  season: string
  selected_customs_zone?: string[]
  sport: { id: string }
  subdepartment: Subdepartment
  super_model: {
    id: number
    labels: CatalogLabels
  }
  universe: Universe
  validation_status?: ValidationStatus
  web_catch_line: string
  wholesale_segmentation?: string
}

export interface ProductHomologationCountry {
  country_code: string
  specific_status?: string
  status: string
  status_comment: string
  last_update_date_time: string
  last_update_user_id: string
}

export interface ProductHomologationByStatus {
  ok: ProductHomologationCountry[]
  nok: ProductHomologationCountry[]
  der: ProductHomologationCountry[]
  undefined: ProductHomologationCountry[]
}

export interface ProductManufacture {
  country_code_design: string
  countries_codes_production: string[]
  countries_homologation: ProductHomologationByStatus
  total_countries: number
}

export type ProductColors = { order: string, id: string }[]

export type GallerySM = GalleryProductBase & {
  model_codes: string[]
}

export interface GalleryModelVariant {
  code: string
  status: 'ACTIVE' | 'CANCELLED'
  size: string
}

export type GalleryModel = GalleryProductBase & {
  model: {
    id: number
    labels: CatalogLabels
  }
  variants: GalleryModelVariant[]
}

export type GalleryProducts<T = GallerySM | GalleryModel> = CatalogPage<T>

export type ProductByViewMode<V extends ViewMode> = V extends 'sm'
  ? GallerySM
  : GalleryModel

export function isGalleryModel(product: GallerySM | GalleryModel): product is GalleryModel {
  return 'model' in product
}

export function isGallerySM(product: GallerySM | GalleryModel): product is GallerySM {
  return 'model_codes' in product
}
